import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, LogOut, QrCode, RefreshCw, ShieldAlert, Zap } from "lucide-react";
import { WA } from "@/constants/testIds";

const STATUS_META = {
  connected:    { label: "Conectado",       cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30", dot: "bg-emerald-500 shadow-[0_0_10px_#10B981]" },
  connecting: { label: "Conectando", cls: "text-amber-400", dot: "bg-amber-500 animate-pulse" },
  qr_pending:   { label: "Aguardando QR",   cls: "bg-amber-500/10 text-amber-400 border-amber-500/30",       dot: "bg-amber-500 animate-pulse" },
  disconnected: { label: "Desconectado",    cls: "bg-slate-500/10 text-slate-400 border-slate-500/30",        dot: "bg-slate-500" },
  banned:       { label: "Banido",          cls: "bg-rose-500/10 text-rose-400 border-rose-500/30",           dot: "bg-rose-500" },
};

export default function MyWhatsAppPage() {
  const [status, setStatus] = useState("disconnected");
  const [qr, setQr] = useState(null);
  const [starting, setStarting] = useState(false);
  const esRef = useRef(null);
  const prevStatus = useRef(status);
  const generation = useRef(0);
  const applySnapshot = (data) => {
    if (data.status) setStatus(data.status);
    if (data.qr) setQr(data.qr);
    else if (["connected", "disconnected", "banned"].includes(data.status)) setQr(null);
  };

  const connect = async () => {
    generation.current += 1;
    setStarting(true);
    try {
      const { data } = await api.post("/whatsapp/start");
      applySnapshot(data);
      openStream();
    } catch {
      toast.error("Falha ao iniciar sessão");
    } finally {
      setStarting(false);
    }
  };

  const openStream = () => {
    if (esRef.current) esRef.current.close();
    // Native EventSource does not send cookies cross-origin unless withCredentials=true
    const es = new EventSource(`${API}/whatsapp/qr`, { withCredentials: true });
    esRef.current = es;
    es.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        applySnapshot(data);
      } catch (_) {}
    };
    es.onerror = () => {
      // Let it auto-retry
    };
  };

  useEffect(() => {
    let active = true;
    let busy = false;
    const refresh = async () => {
      if (busy) return;
      busy = true;
      const revision = generation.current;
      try {
        const { data } = await api.get("/whatsapp/status");
        if (!active || revision !== generation.current) return;
        applySnapshot(data);
        if (data.status && data.status !== "disconnected" && !esRef.current) openStream();
      } catch { /* Live updates keep working while a status request fails. */ }
      finally { busy = false; }
    };
    refresh();
    const timer = setInterval(refresh, 2500);
    return () => {
      active = false;
      clearInterval(timer);
      if (esRef.current) esRef.current.close();
      esRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (prevStatus.current === "connected" && (status === "disconnected" || status === "banned")) {
      toast.error("Sessão do WhatsApp caiu", {
        description: status === "banned" ? "Número banido pelo WhatsApp." : "Reconecte para continuar recebendo mensagens.",
        action: { label: "Reconectar", onClick: () => connect() },
      });
    }
    prevStatus.current = status;
  }, [status]);

  const disconnect = async () => {
    try {
      generation.current += 1;
      await api.post("/whatsapp/logout");
      generation.current += 1;
      toast.success("Sessão encerrada");
      setStatus("disconnected");
      setQr(null);
      if (esRef.current) esRef.current.close();
      esRef.current = null;
    } catch {
      toast.error("Não foi possível desconectar");
    }
  };

  const meta = STATUS_META[status] || STATUS_META.disconnected;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Conversas</h1>
          <p className="text-slate-400 mt-2">Escaneie o QR pra conectar seu número. Envios com delay humanizado 3–8s.</p>
        </div>
        <Badge variant="outline" data-testid={WA.statusBadge} className={`gap-2 px-3 py-1.5 ${meta.cls}`}>
          <span className={`inline-block w-2 h-2 rounded-full ${meta.dot}`} />
          <span className="text-xs font-mono uppercase tracking-wider">{meta.label}</span>
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid={WA.qrContainer} className="bg-[#111827] border border-emerald-500/30 shadow-[0_0_40px_rgba(16,185,129,0.08)] p-8 rounded-2xl relative overflow-hidden">
          <div aria-hidden className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-4">
              <QrCode className="w-5 h-5 text-emerald-400" />
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500">QR Code ao vivo</div>
            </div>

            <div className="grid place-items-center min-h-[340px]">
              {status === "connected" ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 grid place-items-center mx-auto mb-4">
                    <Zap className="w-7 h-7 text-emerald-400" />
                  </div>
                  <div className="font-semibold text-lg">Você está conectado</div>
                  <div className="text-sm text-slate-400 mt-1">Seu número está ativo e recebendo mensagens.</div>
                </div>
              ) : status === "banned" ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-rose-500/10 border border-rose-500/30 grid place-items-center mx-auto mb-4">
                    <ShieldAlert className="w-7 h-7 text-rose-400" />
                  </div>
                  <div className="font-semibold text-lg">Número banido</div>
                  <div className="text-sm text-slate-400 mt-1 max-w-sm">O WhatsApp bloqueou esta sessão. Você pode tentar novamente com outro número.</div>
                </div>
              ) : qr ? (
                <div className="p-4 bg-white rounded-xl">
                  <img src={qr} alt="QR Code" className="w-64 h-64" />
                </div>
              ) : (
                <div className="text-center">
                  {(starting || status !== "disconnected") && <Loader2 className="w-8 h-8 animate-spin text-emerald-400 mx-auto mb-3" />}
                  <div className="text-sm text-slate-400">{status === "disconnected" && !starting ? "Clique em Gerar QR Code para conectar." : "Aguardando QR..."}</div>
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              {status === "disconnected" && (
                <Button onClick={connect} disabled={starting} data-testid={WA.startButton}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white active:scale-95 transition">
                  {starting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <QrCode className="w-4 h-4 mr-2" />}
                  Gerar QR Code
                </Button>
              )}
              {status === "banned" && (
                <Button onClick={connect} data-testid={WA.reconnectButton}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white">
                  <RefreshCw className="w-4 h-4 mr-2" /> Tentar novamente
                </Button>
              )}
              {(status === "connected" || status === "qr_pending") && (
                <Button variant="outline" onClick={disconnect} data-testid={WA.disconnectButton}
                        className="border-rose-500/30 text-rose-400 hover:bg-rose-500/10">
                  <LogOut className="w-4 h-4 mr-2" /> Desconectar
                </Button>
              )}
            </div>
          </div>
        </Card>

        <Card className="bg-[#111827] border-slate-800 p-8">
          <h3 className="text-lg font-semibold mb-4">Como funciona</h3>
          <ol className="space-y-4 text-sm text-slate-300">
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-emerald-500/10 text-emerald-400 grid place-items-center text-xs font-mono flex-none">1</span>
              Toque em <b>Gerar QR Code</b> para iniciar a sessão isolada do seu usuário.
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-emerald-500/10 text-emerald-400 grid place-items-center text-xs font-mono flex-none">2</span>
              Abra o WhatsApp no celular → Aparelhos conectados → Conectar um aparelho.
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-emerald-500/10 text-emerald-400 grid place-items-center text-xs font-mono flex-none">3</span>
              Aponte a câmera para o QR. O status muda para <b className="text-emerald-400">Conectado</b>.
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-emerald-500/10 text-emerald-400 grid place-items-center text-xs font-mono flex-none">4</span>
              Suas mensagens começam a fluir. Envios usam delay 3–8s pra parecer humano.
            </li>
          </ol>
          <div className="mt-6 pt-6 border-t border-slate-800 text-xs font-mono text-slate-500">
            Opt-out automático via &ldquo;sair&rdquo; ou &ldquo;parar&rdquo; · LGPD-friendly
          </div>
        </Card>
      </div>
    </div>
  );
}

